/**
 * Google Drive integration service.
 * Supports read-only project browsing/packaging with incremental escalation
 * to write scope only for explicit save/edit actions.
 */

import { loadGoogleDriveConfig } from '../utils/envConfig';

const GOOGLE_API_SCRIPT_URL = 'https://apis.google.com/js/api.js';
const GOOGLE_ID_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

const DRIVE_READ_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
const DRIVE_WRITE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

const DEFAULT_CHUNK_POLICY = {
  directReadMaxBytes: 32 * 1024 * 1024,
  chunkedReadMaxBytes: 2 * 1024 * 1024 * 1024,
  chunkSizeBytes: 8 * 1024 * 1024,
  maxRetriesPerChunk: 5,
  initialBackoffMs: 1000,
  maxBackoffMs: 30000
};

const GOOGLE_DOCS_MIME_PREFIX = 'application/vnd.google-apps.';
const GOOGLE_SHORTCUT_MIME = 'application/vnd.google-apps.shortcut';
const GOOGLE_FOLDER_MIME = 'application/vnd.google-apps.folder';

const GOOGLE_EXPORT_MIME_MAP = {
  'application/vnd.google-apps.document': {
    mimeType: 'text/plain',
    extension: '.txt'
  },
  'application/vnd.google-apps.spreadsheet': {
    mimeType: 'text/csv',
    extension: '.csv'
  },
  'application/vnd.google-apps.presentation': {
    mimeType: 'text/plain',
    extension: '.txt'
  }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const getExtension = (name = '') => {
  if (!name.includes('.')) {
    return '';
  }

  return name.slice(name.lastIndexOf('.')).toLowerCase();
};

class GoogleDriveService {
  constructor() {
    this.config = loadGoogleDriveConfig();
    this.clientId = this.config.clientId;
    this.isConfigured = this.config.isConfigured;

    console.log('[GoogleDriveService] Initialized', {
      isConfigured: this.isConfigured,
      hasClientId: Boolean(this.clientId)
    });

    if (!this.isConfigured) {
      console.warn('[GoogleDriveService] Google Drive integration is disabled because REACT_APP_GOOGLE_CLIENT_ID is missing.');
      console.warn('[GoogleDriveService] If you set this variable recently, please restart your development server or rebuild the application.');
    }

    this.accessToken = '';
    this.grantedScopes = new Set();
    this.tokenExpiryEpochMs = 0;

    this.googleApiScriptPromise = null;
    this.googleIdentityScriptPromise = null;
    this.gapiPickerInitPromise = null;

    this.tokenClient = null;
    this.currentFolder = null;

    this.tokenRequestResolver = null;
    this.tokenRequestRejecter = null;
  }

  isReady() {
    return this.isConfigured;
  }

  getReadScope() {
    return DRIVE_READ_SCOPE;
  }

  getWriteScope() {
    return DRIVE_WRITE_SCOPE;
  }

  getCurrentFolder() {
    return this.currentFolder;
  }

  setCurrentFolder(folder) {
    this.currentFolder = folder || null;
  }

  async ensureInitialized() {
    if (!this.isConfigured) {
      throw this.createError('GOOGLE_DRIVE_NOT_CONFIGURED', 'Google Drive is not configured. Set REACT_APP_GOOGLE_CLIENT_ID.');
    }

    if (!this.googleApiScriptPromise) {
      this.googleApiScriptPromise = this.loadScript(GOOGLE_API_SCRIPT_URL, 'gapi');
    }

    if (!this.googleIdentityScriptPromise) {
      this.googleIdentityScriptPromise = this.loadScript(GOOGLE_ID_SCRIPT_URL, 'google');
    }

    await Promise.all([this.googleApiScriptPromise, this.googleIdentityScriptPromise]);

    if (!this.gapiPickerInitPromise) {
      this.gapiPickerInitPromise = new Promise((resolve, reject) => {
        if (!window.gapi || !window.gapi.load) {
          reject(this.createError('GOOGLE_API_UNAVAILABLE', 'Google API client failed to load.'));
          return;
        }

        window.gapi.load('picker', {
          callback: () => resolve(true),
          onerror: () => reject(this.createError('GOOGLE_PICKER_UNAVAILABLE', 'Google Picker failed to initialize.'))
        });
      });
    }

    await this.gapiPickerInitPromise;
  }

  loadScript(src, globalName) {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(this.createError('WINDOW_UNAVAILABLE', 'Browser window is not available.'));
        return;
      }

      if (window[globalName]) {
        resolve(true);
        return;
      }

      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true), { once: true });
        existingScript.addEventListener('error', () => reject(this.createError('GOOGLE_SCRIPT_LOAD_FAILED', `Failed loading ${src}`)), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(this.createError('GOOGLE_SCRIPT_LOAD_FAILED', `Failed loading ${src}`));
      document.head.appendChild(script);
    });
  }

  getOrCreateTokenClient() {
    if (this.tokenClient) {
      return this.tokenClient;
    }

    if (!window.google?.accounts?.oauth2?.initTokenClient) {
      throw this.createError('GOOGLE_IDENTITY_UNAVAILABLE', 'Google Identity Services is unavailable.');
    }

    this.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: this.clientId,
      scope: DRIVE_READ_SCOPE,
      callback: (response) => this.handleTokenResponse(response),
      error_callback: (error) => {
        this.handleTokenError({
          error: error?.type || 'token_client_error',
          error_description: error?.message || 'Token client failed to acquire a token.'
        });
      }
    });

    return this.tokenClient;
  }

  handleTokenResponse(response) {
    if (this.tokenRequestResolver) {
      if (!response) {
        this.handleTokenError({ error: 'GOOGLE_TOKEN_EMPTY', error_description: 'Google token callback returned no response.' });
        return;
      }

      if (response.error) {
        this.handleTokenError(response);
        return;
      }

      this.tokenRequestResolver(response);
    }
    this.cleanupTokenRequest();
  }

  handleTokenError(errorData) {
    if (this.tokenRequestRejecter) {
      if (errorData.error === 'popup_closed_by_user' || errorData.error === 'access_denied') {
        this.tokenRequestRejecter(this.createError('GOOGLE_AUTH_CANCELLED', errorData.error_description || 'Google authorization was cancelled.'));
      } else if (errorData.error === 'popup_failed_to_open') {
        this.tokenRequestRejecter(this.createError('GOOGLE_POPUP_BLOCKED', 'Google sign-in popup was blocked by the browser.'));
      } else {
        this.tokenRequestRejecter(this.createError('GOOGLE_TOKEN_ERROR', errorData.error_description || errorData.error));
      }
    }
    this.cleanupTokenRequest();
  }

  cleanupTokenRequest() {
    this.tokenRequestResolver = null;
    this.tokenRequestRejecter = null;
  }

  hasScope(scope) {
    return this.grantedScopes.has(scope);
  }

  isTokenStillValid() {
    if (!this.accessToken) {
      return false;
    }

    return Date.now() + 10_000 < this.tokenExpiryEpochMs;
  }

  async signInReadOnly() {
    if (this.hasScope(DRIVE_READ_SCOPE) && this.isTokenStillValid()) {
      return {
        accessToken: this.accessToken,
        grantedScopes: Array.from(this.grantedScopes)
      };
    }

    await this.ensureInitialized();

    const tokenResponse = await this.requestToken({
      scope: DRIVE_READ_SCOPE,
      // First-time auth should show consent, refreshes should stay silent.
      prompt: this.hasScope(DRIVE_READ_SCOPE) ? '' : 'consent'
    });

    this.applyTokenResponse(tokenResponse);

    return {
      accessToken: this.accessToken,
      grantedScopes: Array.from(this.grantedScopes)
    };
  }

  async ensureWriteAccess() {
    if (this.hasScope(DRIVE_WRITE_SCOPE) && this.isTokenStillValid()) {
      return true;
    }

    await this.ensureInitialized();

    const tokenResponse = await this.requestToken({
      scope: `${DRIVE_READ_SCOPE} ${DRIVE_WRITE_SCOPE}`,
      prompt: 'consent'
    });

    this.applyTokenResponse(tokenResponse);

    return this.hasScope(DRIVE_WRITE_SCOPE);
  }

  applyTokenResponse(tokenResponse) {
    if (!tokenResponse?.access_token) {
      throw this.createError('GOOGLE_TOKEN_MISSING', 'Google token response was missing access_token.');
    }

    this.accessToken = tokenResponse.access_token;
    const expiresInSeconds = Number(tokenResponse.expires_in || 3600);
    this.tokenExpiryEpochMs = Date.now() + (expiresInSeconds * 1000);

    const scopeSet = new Set((tokenResponse.scope || '').split(/\s+/).map(item => item.trim()).filter(Boolean));
    if (scopeSet.size === 0) {
      scopeSet.add(DRIVE_READ_SCOPE);
    }

    for (const scope of scopeSet) {
      this.grantedScopes.add(scope);
    }
  }

  requestToken({ scope, prompt }) {
    if (this.tokenRequestResolver) {
      return Promise.reject(this.createError('GOOGLE_AUTH_IN_PROGRESS', 'Authentication already in progress.'));
    }

    return new Promise((resolve, reject) => {
      this.tokenRequestResolver = resolve;
      this.tokenRequestRejecter = reject;

      try {
        const tokenClient = this.getOrCreateTokenClient();
        tokenClient.requestAccessToken({ scope, prompt });
      } catch (error) {
        this.handleTokenError({ error: 'GOOGLE_TOKEN_ERROR', error_description: error.message });
      }
    });
  }

  async openPickerWithRetry(options = {}) {
    const {
      maxAttempts = 2,
      fallbackFolder = this.currentFolder,
      allowFallbackOnCancel = false
    } = options;

    await this.signInReadOnly();

    let lastError = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const folder = await this.openFolderPicker();
        this.currentFolder = folder;
        return {
          folder,
          usedFallback: false,
          fallbackReason: null
        };
      } catch (error) {
        lastError = error;

        const code = this.getErrorCode(error);
        if (code === 'GOOGLE_PICKER_CANCELLED' && !allowFallbackOnCancel) {
          throw error;
        }

        if (!this.isRetryablePickerError(error)) {
          break;
        }
      }
    }

    if (fallbackFolder?.id) {
      this.currentFolder = fallbackFolder;
      return {
        folder: fallbackFolder,
        usedFallback: true,
        fallbackReason: this.getErrorCode(lastError)
      };
    }

    throw lastError || this.createError('GOOGLE_PICKER_FAILED', 'Google Picker failed.');
  }

  isRetryablePickerError(error) {
    const code = this.getErrorCode(error);
    return code === 'GOOGLE_PICKER_FAILED' || code === 'GOOGLE_POPUP_BLOCKED';
  }

  openFolderPicker() {
    return new Promise((resolve, reject) => {
      if (!window.google?.picker?.PickerBuilder) {
        reject(this.createError('GOOGLE_PICKER_UNAVAILABLE', 'Google Picker API is unavailable.'));
        return;
      }

      let isSettled = false;
      const settleResolve = (value) => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        resolve(value);
      };
      const settleReject = (error) => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        reject(error);
      };

      const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setOwnedByMe(true);

      const picker = new window.google.picker.PickerBuilder()
        .setOAuthToken(this.accessToken)
        .addView(docsView)
        .setTitle('Select Google Drive Project Folder')
        .setCallback((data) => {
          const action = data?.action;

          if (action === window.google.picker.Action.CANCEL) {
            settleReject(this.createError('GOOGLE_PICKER_CANCELLED', 'Folder selection was cancelled.'));
            return;
          }

          if (action !== window.google.picker.Action.PICKED) {
            // Ignore intermediate callback actions (for example LOADED).
            return;
          }

          if (!Array.isArray(data.docs) || data.docs.length === 0) {
            settleReject(this.createError('GOOGLE_PICKER_FAILED', 'Google Picker did not return a folder selection.'));
            return;
          }

          const [selected] = data.docs;
          settleResolve({
            id: selected.id,
            name: selected.name || selected.id
          });
        })
        .build();

      picker.setVisible(true);
    });
  }

  async listFolderContents(folderId = 'root') {
    await this.signInReadOnly();

    const query = new URLSearchParams({
      q: `'${folderId}' in parents and trashed = false`,
      fields: 'files(id, name, mimeType, size, modifiedTime, shortcutDetails)',
      pageSize: '1000',
      orderBy: 'folder,name'
    });

    // Keep root browsing in the signed-in user's My Drive; only widen scope when
    // navigating inside a concrete folder (for example a previously selected Shared Drive folder).
    if (folderId !== 'root') {
      query.set('includeItemsFromAllDrives', 'true');
      query.set('supportsAllDrives', 'true');
    }

    const response = await this.authorizedFetch(`https://www.googleapis.com/drive/v3/files?${query.toString()}`);

    if (!response.ok) {
      const payload = await this.tryJson(response);
      throw this.createError('GOOGLE_DRIVE_LIST_FAILED', payload?.error?.message || 'Failed to list Drive folder contents.');
    }

    const payload = await response.json();
    const entries = Array.isArray(payload.files) ? payload.files : [];

    return entries.map(entry => {
      if (entry.mimeType === GOOGLE_FOLDER_MIME) {
        return {
          id: entry.id,
          name: entry.name,
          type: 'folder',
          mimeType: entry.mimeType
        };
      }

      // Check for shortcuts or unsupported types if necessary, using similar logic to resolveDriveEntry
      if (entry.mimeType === GOOGLE_SHORTCUT_MIME) {
         // simplified shortcut handling for browsing
         return {
             id: entry.shortcutDetails?.targetId || entry.id,
             name: entry.name,
             type: 'shortcut',
             mimeType: entry.shortcutDetails?.targetMimeType || GOOGLE_SHORTCUT_MIME
         };
      }

      const descriptor = this.createFileDescriptor(entry, '');
      if (descriptor.kind === 'file') {
          return {
              ...descriptor.file,
              type: 'file'
          };
      }

      return {
          id: entry.id,
          name: entry.name,
          type: 'unknown',
          mimeType: entry.mimeType
      };
    });
  }

  async listFolderFilesRecursive(folderId, options = {}) {
    const {
      includeWarnings = true,
      parentPath = ''
    } = options;

    const warnings = [];
    const files = [];

    const queue = [{ id: folderId, path: parentPath }];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) {
        break;
      }

      let pageToken = '';
      do {
        const query = new URLSearchParams({
          q: `'${current.id}' in parents and trashed = false`,
          fields: 'nextPageToken, files(id,name,mimeType,size,modifiedTime,shortcutDetails)',
          pageSize: '1000',
          includeItemsFromAllDrives: 'true',
          supportsAllDrives: 'true',
          corpora: 'allDrives'
        });

        if (pageToken) {
          query.set('pageToken', pageToken);
        }

        const response = await this.authorizedFetch(`https://www.googleapis.com/drive/v3/files?${query.toString()}`);

        if (!response.ok) {
          if (response.status === 403 || response.status === 404) {
            if (includeWarnings) {
              warnings.push({
                code: 'GOOGLE_DRIVE_FOLDER_PARTIAL_ACCESS',
                message: `Some items in folder ${current.id} are not accessible with current permissions.`
              });
              break;
            }
          }

          const payload = await this.tryJson(response);
          throw this.createError('GOOGLE_DRIVE_LIST_FAILED', payload?.error?.message || 'Failed to list Drive folder contents.');
        }

        const payload = await response.json();
        const entries = Array.isArray(payload.files) ? payload.files : [];

        for (const entry of entries) {
          const resolved = this.resolveDriveEntry(entry, current.path);
          if (!resolved) {
            continue;
          }

          if (resolved.kind === 'directory') {
            queue.push({ id: resolved.id, path: resolved.path });
            continue;
          }

          if (resolved.kind === 'warning') {
            if (includeWarnings) {
              warnings.push(resolved.warning);
            }
            continue;
          }

          files.push(resolved.file);
        }

        pageToken = payload.nextPageToken || '';
      } while (pageToken);
    }

    files.sort((a, b) => a.path.localeCompare(b.path));

    return { files, warnings };
  }

  resolveDriveEntry(entry, parentPath) {
    if (entry.mimeType === GOOGLE_FOLDER_MIME) {
      return {
        kind: 'directory',
        id: entry.id,
        path: parentPath ? `${parentPath}/${entry.name}` : entry.name
      };
    }

    if (entry.mimeType === GOOGLE_SHORTCUT_MIME) {
      const targetMimeType = entry.shortcutDetails?.targetMimeType;
      if (!targetMimeType || targetMimeType === GOOGLE_FOLDER_MIME) {
        return {
          kind: 'warning',
          warning: {
            code: 'GOOGLE_DRIVE_SHORTCUT_UNSUPPORTED',
            message: `Skipping shortcut ${entry.name}; target is unavailable for packaging.`
          }
        };
      }

      return this.createFileDescriptor({
        ...entry,
        id: entry.shortcutDetails?.targetId || entry.id,
        mimeType: targetMimeType,
        name: entry.name
      }, parentPath);
    }

    return this.createFileDescriptor(entry, parentPath);
  }

  createFileDescriptor(entry, parentPath) {
    const fullPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
    const googleWorkspace = typeof entry.mimeType === 'string' && entry.mimeType.startsWith(GOOGLE_DOCS_MIME_PREFIX);
    const exportInfo = googleWorkspace ? GOOGLE_EXPORT_MIME_MAP[entry.mimeType] : null;

    if (googleWorkspace && !exportInfo) {
      return {
        kind: 'warning',
        warning: {
          code: 'GOOGLE_WORKSPACE_EXPORT_UNSUPPORTED',
          message: `Skipping unsupported Google Workspace file type: ${entry.name}`
        }
      };
    }

    const fileName = exportInfo && !entry.name.endsWith(exportInfo.extension)
      ? `${entry.name}${exportInfo.extension}`
      : entry.name;

    const finalPath = fullPath.endsWith(fileName) ? fullPath : `${parentPath ? `${parentPath}/` : ''}${fileName}`;

    return {
      kind: 'file',
      file: {
        name: fileName,
        path: finalPath,
        type: 'file',
        size: Number(entry.size || 0),
        lastModified: entry.modifiedTime ? new Date(entry.modifiedTime) : new Date(),
        extension: getExtension(fileName),
        mimeType: exportInfo?.mimeType || entry.mimeType,
        source: 'google-drive',
        driveMeta: {
          id: entry.id,
          mimeType: entry.mimeType,
          exportMimeType: exportInfo?.mimeType,
          isGoogleWorkspaceFile: Boolean(googleWorkspace)
        }
      }
    };
  }

  async readFile(fileItem, options = {}) {
    const driveMeta = fileItem?.driveMeta;
    if (!driveMeta?.id) {
      throw this.createError('GOOGLE_DRIVE_FILE_ID_MISSING', `Drive file id missing for ${fileItem?.name || 'file'}.`);
    }

    await this.signInReadOnly();

    const chunkPolicy = {
      ...DEFAULT_CHUNK_POLICY,
      ...(options.chunkPolicy || {})
    };

    if (options.requireRawBlob) {
      return this.downloadAsBlob(fileItem, chunkPolicy, options.onProgress, options.onRetry);
    }

    const blob = await this.downloadAsBlob(fileItem, chunkPolicy, options.onProgress, options.onRetry);
    return this.parseDriveFileContent(blob, fileItem);
  }

  async downloadAsBlob(fileItem, chunkPolicy, onProgress, onRetry) {
    const size = Number(fileItem.size || 0);
    const driveMeta = fileItem.driveMeta;

    if (size > chunkPolicy.chunkedReadMaxBytes) {
      throw this.createError('GOOGLE_DRIVE_FILE_TOO_LARGE', `${fileItem.name} exceeds browser-safe streaming limits.`);
    }

    if (!size || size <= chunkPolicy.directReadMaxBytes || driveMeta.isGoogleWorkspaceFile) {
      const blob = await this.downloadWholeFile(fileItem);
      onProgress?.(100);
      return blob;
    }

    return this.downloadFileInChunks(fileItem, chunkPolicy, onProgress, onRetry);
  }

  async downloadWholeFile(fileItem) {
    const driveMeta = fileItem.driveMeta;
    const endpoint = driveMeta.isGoogleWorkspaceFile
      ? `https://www.googleapis.com/drive/v3/files/${driveMeta.id}/export?mimeType=${encodeURIComponent(driveMeta.exportMimeType || 'text/plain')}`
      : `https://www.googleapis.com/drive/v3/files/${driveMeta.id}?alt=media&supportsAllDrives=true`;

    const response = await this.authorizedFetch(endpoint);
    if (!response.ok) {
      const payload = await this.tryJson(response);
      throw this.createError('GOOGLE_DRIVE_DOWNLOAD_FAILED', payload?.error?.message || `Failed downloading ${fileItem.name}`);
    }

    return await response.blob();
  }

  async downloadFileInChunks(fileItem, chunkPolicy, onProgress, onRetry) {
    const size = Number(fileItem.size || 0);
    const chunks = [];
    let bytesDownloaded = 0;

    for (let start = 0; start < size; start += chunkPolicy.chunkSizeBytes) {
      const end = Math.min(start + chunkPolicy.chunkSizeBytes - 1, size - 1);
      const buffer = await this.downloadChunkWithRetry(fileItem, start, end, chunkPolicy, onRetry);
      chunks.push(buffer);
      bytesDownloaded += (end - start + 1);
      onProgress?.(Math.min(100, Math.round((bytesDownloaded / size) * 100)));
    }

    return new Blob(chunks, { type: fileItem.mimeType || 'application/octet-stream' });
  }

  async downloadChunkWithRetry(fileItem, start, end, chunkPolicy, onRetry) {
    const endpoint = `https://www.googleapis.com/drive/v3/files/${fileItem.driveMeta.id}?alt=media&supportsAllDrives=true`;

    for (let attempt = 1; attempt <= chunkPolicy.maxRetriesPerChunk; attempt += 1) {
      const response = await this.authorizedFetch(endpoint, {
        headers: {
          Range: `bytes=${start}-${end}`
        }
      });

      if (response.ok || response.status === 206) {
        return await response.arrayBuffer();
      }

      if (attempt >= chunkPolicy.maxRetriesPerChunk) {
        const payload = await this.tryJson(response);
        throw this.createError(
          'GOOGLE_DRIVE_CHUNK_RETRY_EXHAUSTED',
          payload?.error?.message || `Failed downloading chunk ${start}-${end} for ${fileItem.name}`,
          {
            fileName: fileItem.name,
            start,
            end,
            attempts: attempt
          }
        );
      }

      const backoff = Math.min(
        chunkPolicy.maxBackoffMs,
        chunkPolicy.initialBackoffMs * (2 ** (attempt - 1))
      );
      onRetry?.({
        fileName: fileItem.name,
        start,
        end,
        attempt,
        backoffMs: backoff
      });
      await sleep(backoff);
    }

    throw this.createError('GOOGLE_DRIVE_CHUNK_RETRY_EXHAUSTED', `Retry exhausted for ${fileItem.name}`);
  }

  async parseDriveFileContent(blob, fileItem) {
    const extension = (fileItem.extension || '').toLowerCase();
    const mimeType = (fileItem.mimeType || '').toLowerCase();

    if (extension === '.json') {
      return JSON.parse(await blob.text());
    }

    if (['.txt', '.tsv', '.csv', '.md'].includes(extension)) {
      const text = await blob.text();
      if (extension === '.tsv' || extension === '.csv') {
        return this.parseDelimited(text);
      }
      return text;
    }

    if (mimeType.startsWith('text/')) {
      return await blob.text();
    }

    // Keep Drive behavior aligned with local file reads: binary content is previewed via object URL.
    return URL.createObjectURL(blob);
  }

  parseDelimited(text) {
    const lines = text.trim().split('\n');
    if (lines.length === 0) {
      return [];
    }

    const delimiter = lines[0].includes('\t') ? '\t' : ',';
    const headers = lines[0].split(delimiter).map(value => value.trim().replace(/^"|"$/g, ''));

    return lines.slice(1).map((line) => {
      const values = line.split(delimiter);
      const row = {};
      headers.forEach((header, index) => {
        const value = (values[index] || '').trim().replace(/^"|"$/g, '');
        const num = Number(value);
        row[header] = Number.isFinite(num) && value !== '' ? num : value;
      });
      return row;
    });
  }

  async saveFile(fileItem, content) {
    if (!fileItem?.driveMeta?.id) {
      throw this.createError('GOOGLE_DRIVE_FILE_ID_MISSING', `Drive file id missing for ${fileItem?.name || 'file'}.`);
    }

    const hasWriteAccess = await this.ensureWriteAccess();
    if (!hasWriteAccess) {
      throw this.createError('GOOGLE_DRIVE_WRITE_SCOPE_REQUIRED', 'Google Drive write scope is required to save changes.');
    }

    if (fileItem.driveMeta.isGoogleWorkspaceFile) {
      throw this.createError('GOOGLE_DRIVE_SAVE_UNSUPPORTED', 'Saving Google Workspace native files is not supported in this flow.');
    }

    const payload = this.normalizeSaveContent(content, fileItem.mimeType);

    const response = await this.authorizedFetch(
      `https://www.googleapis.com/upload/drive/v3/files/${fileItem.driveMeta.id}?uploadType=media&supportsAllDrives=true`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': payload.type || 'application/octet-stream'
        },
        body: payload
      }
    );

    if (!response.ok) {
      const body = await this.tryJson(response);
      throw this.createError('GOOGLE_DRIVE_SAVE_FAILED', body?.error?.message || `Failed to save ${fileItem.name}`);
    }

    return true;
  }

  normalizeSaveContent(content, mimeType) {
    if (content instanceof Blob) {
      return content;
    }

    if (content instanceof ArrayBuffer || ArrayBuffer.isView(content)) {
      return new Blob([content], { type: mimeType || 'application/octet-stream' });
    }

    if (Array.isArray(content)) {
      const headers = Object.keys(content[0] || {});
      const lines = [headers.join('\t')].concat(
        content.map(row => headers.map(key => String(row[key] ?? '')).join('\t'))
      );
      return new Blob([lines.join('\n')], { type: 'text/tab-separated-values;charset=utf-8' });
    }

    if (typeof content === 'object' && content !== null) {
      return new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
    }

    return new Blob([String(content ?? '')], { type: mimeType || 'text/plain;charset=utf-8' });
  }

  async authorizedFetch(url, init = {}) {
    if (!this.isTokenStillValid()) {
      await this.signInReadOnly();
    }

    const headers = new Headers(init.headers || {});
    headers.set('Authorization', `Bearer ${this.accessToken}`);

    return fetch(url, {
      ...init,
      headers
    });
  }

  async tryJson(response) {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  getErrorCode(error) {
    if (!error) {
      return 'UNKNOWN';
    }

    if (typeof error === 'object' && 'code' in error) {
      return error.code;
    }

    return 'UNKNOWN';
  }

  createError(code, message, details) {
    const error = new Error(message);
    error.code = code;
    if (details) {
      error.details = details;
    }
    return error;
  }
}

export {
  DRIVE_READ_SCOPE,
  DRIVE_WRITE_SCOPE,
  DEFAULT_CHUNK_POLICY
};

export default GoogleDriveService;

